import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link, NavLink as RouterNavLink } from 'react-router-dom';
import { Image } from 'semantic-ui-react';
import { Menu } from '../../layout/Menu';
import { device } from '../../../theme';
import {
  LineChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Import NSFF statistics data
let nsffDataRaw = [];
try {
  nsffDataRaw = require('../../../data/nsff-statistics.json');
} catch (e) {
  console.warn('NSFF statistics data not yet generated. Run: npm run generate-nsff-statistics');
}

// Filter out incomplete/skewed years (2017 and 2019 were data imports)
const nsffData = nsffDataRaw.filter(item => item.year >= 2020);

const COLORS = {
  male: '#4A90E2',
  female: '#E94B9A',
  unknown: '#999'
};

const AGE_COLORS = ['#90b361', '#7a9851', '#648041', '#4e6831', '#385021', '#709050', '#567040', '#3c5830'];

export const NSFFStatistics = () => {
  const [hiddenMembershipLines, setHiddenMembershipLines] = React.useState({});
  const [hiddenGenderBars, setHiddenGenderBars] = React.useState({});
  const [hiddenAgeBars, setHiddenAgeBars] = React.useState({});
  const [sortColumn, setSortColumn] = React.useState('year');
  const [sortDirection, setSortDirection] = React.useState('desc');

  const handleMembershipLegendClick = (e) => {
    setHiddenMembershipLines(prev => ({
      ...prev,
      [e.dataKey]: !prev[e.dataKey]
    }));
  };

  const handleGenderLegendClick = (e) => {
    setHiddenGenderBars(prev => ({
      ...prev,
      [e.dataKey]: !prev[e.dataKey]
    }));
  };

  const handleAgeLegendClick = (e) => {
    setHiddenAgeBars(prev => ({
      ...prev,
      [e.dataKey]: !prev[e.dataKey]
    }));
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedTableData = useMemo(() => {
    const sorted = [...nsffData];
    sorted.sort((a, b) => {
      let aVal, bVal;

      // Handle nested properties (age groups)
      if (sortColumn.includes('-') || sortColumn.includes('+')) {
        aVal = a.ageGroups[sortColumn];
        bVal = b.ageGroups[sortColumn];
      } else {
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }

      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (sortDirection === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
    return sorted;
  }, [sortColumn, sortDirection]);

  const membershipData = useMemo(() => {
    return nsffData.map(item => ({
      year: item.year,
      'Aktive medlemmer': item.totalActiveMembers,
      'Nye medlemmer': item.newMembers,
      'Førstegangsmedlemmer': item.firstTimeMembers,
      'Aktive klubber': item.activeClubs
    }));
  }, []);

  const genderData = useMemo(() => {
    return nsffData.map(item => ({
      year: item.year,
      'Menn': item.maleCount,
      'Kvinner': item.femaleCount
    }));
  }, []);

  const latestYear = nsffData.length > 0 ? nsffData[nsffData.length - 1] : null;

  const genderPieData = useMemo(() => {
    if (!latestYear) return [];
    return [
      { name: 'Menn', value: latestYear.maleCount },
      { name: 'Kvinner', value: latestYear.femaleCount },
      { name: 'Ukjent', value: latestYear.unknownGenderCount }
    ];
  }, [latestYear]);

  const ageDistributionData = useMemo(() => {
    return nsffData.map(item => ({
      year: item.year,
      '5-15': item.ageGroups['5-15'],
      '16-25': item.ageGroups['16-25'],
      '26-35': item.ageGroups['26-35'],
      '36-50': item.ageGroups['36-50'],
      '51-60': item.ageGroups['51-60'],
      '61-70': item.ageGroups['61-70'],
      '71-80': item.ageGroups['71-80'],
      '81+': item.ageGroups['81+']
    }));
  }, []);

  const latestAgePieData = useMemo(() => {
    if (!latestYear) return [];
    return Object.entries(latestYear.ageGroups)
      .filter(([key]) => key !== 'unknown')
      .map(([key, value]) => ({ name: key, value }));
  }, [latestYear]);

  return (
    <>
      <Header>
        <div>
          <a href="https://nsff.no">
            <Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo" height={"60"} />
          </a>
        </div>
        <div>
          <h1>NSFF Statistikk</h1>
        </div>
        <div>
          <Link to={"/"}>Tilbake</Link>
        </div>
      </Header>
      <Menu />
      <Container>
        <ContentWrapper>
          <Title>NSFF Organisasjonsstatistikk</Title>

          <NavLinks>
            <NavLink to="/statistikk" end>Konkurranser</NavLink>
            <NavLink to="/statistikk/personer">Personer</NavLink>
            <NavLink to="/statistikk/klubber">Klubber</NavLink>
            <NavLink to="/statistikk/hederstegn">Hederstegn</NavLink>
            <NavLink to="/statistikk/nsff">NSFF</NavLink>
          </NavLinks>

          {nsffData.length > 0 && (
            <>
              <TwoColumnSection>
                <ChartSection>
                  <ChartTitle>Aldersfordeling {latestYear?.year}</ChartTitle>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={latestAgePieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {latestAgePieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={AGE_COLORS[index % AGE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartSection>

                <StatsCard>
                  <h3>Nøkkeltall {latestYear?.year}</h3>
                  <StatRow>
                    <StatLabel>Totalt aktive medlemmer:</StatLabel>
                    <StatValue>{latestYear?.totalActiveMembers}</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Aktive klubber:</StatLabel>
                    <StatValue>{latestYear?.activeClubs}</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Nye medlemmer:</StatLabel>
                    <StatValue>{latestYear?.newMembers}</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Førstegangsmedlemmer:</StatLabel>
                    <StatValue>{latestYear?.firstTimeMembers}</StatValue>
                  </StatRow>
                  <Divider />
                  <StatRow>
                    <StatLabel>Menn:</StatLabel>
                    <StatValue>{latestYear?.maleCount} ({((latestYear?.maleCount / latestYear?.totalActiveMembers) * 100).toFixed(1)}%)</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Kvinner:</StatLabel>
                    <StatValue>{latestYear?.femaleCount} ({((latestYear?.femaleCount / latestYear?.totalActiveMembers) * 100).toFixed(1)}%)</StatValue>
                  </StatRow>
                  <Divider />
                  <StatRow>
                    <StatLabel>Ungdom (5-25 år):</StatLabel>
                    <StatValue>{(latestYear?.ageGroups['5-15'] || 0) + (latestYear?.ageGroups['16-25'] || 0)} ({(((latestYear?.ageGroups['5-15'] + latestYear?.ageGroups['16-25']) / latestYear?.totalActiveMembers) * 100).toFixed(1)}%)</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Over 60 år:</StatLabel>
                    <StatValue>{(latestYear?.ageGroups['61-70'] || 0) + (latestYear?.ageGroups['71-80'] || 0) + (latestYear?.ageGroups['81+'] || 0)} ({(((latestYear?.ageGroups['61-70'] + latestYear?.ageGroups['71-80'] + latestYear?.ageGroups['81+']) / latestYear?.totalActiveMembers) * 100).toFixed(1)}%)</StatValue>
                  </StatRow>
                </StatsCard>
              </TwoColumnSection>

              <ChartSection>
                <ChartTitle>Medlemsutvikling</ChartTitle>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={membershipData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Legend onClick={handleMembershipLegendClick} wrapperStyle={{ cursor: 'pointer' }} />
                    <Line type="monotone" dataKey="Aktive medlemmer" stroke="#36501c" strokeWidth={3} hide={hiddenMembershipLines['Aktive medlemmer']} />
                    <Line type="monotone" dataKey="Nye medlemmer" stroke="#90b361" strokeWidth={2} hide={hiddenMembershipLines['Nye medlemmer']} />
                    <Line type="monotone" dataKey="Førstegangsmedlemmer" stroke="#648041" strokeWidth={2} strokeDasharray="5 5" hide={hiddenMembershipLines['Førstegangsmedlemmer']} />
                    <Line type="monotone" dataKey="Aktive klubber" stroke="#7a9851" strokeWidth={2} hide={hiddenMembershipLines['Aktive klubber']} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartSection>

              <TwoColumnSection>
                <ChartSection>
                  <ChartTitle>Kjønnsfordeling over tid</ChartTitle>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={genderData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis />
                      <Tooltip />
                      <Legend onClick={handleGenderLegendClick} wrapperStyle={{ cursor: 'pointer' }} />
                      <Bar dataKey="Menn" fill={COLORS.male} hide={hiddenGenderBars['Menn']} />
                      <Bar dataKey="Kvinner" fill={COLORS.female} hide={hiddenGenderBars['Kvinner']} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartSection>

                <ChartSection>
                  <ChartTitle>Kjønnsfordeling {latestYear?.year}</ChartTitle>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={genderPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill={COLORS.male} />
                        <Cell fill={COLORS.female} />
                        <Cell fill={COLORS.unknown} />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartSection>
              </TwoColumnSection>

              <ChartSection>
                <ChartTitle>Aldersfordeling over tid</ChartTitle>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={ageDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Legend onClick={handleAgeLegendClick} wrapperStyle={{ cursor: 'pointer' }} />
                    <Bar dataKey="5-15" stackId="a" fill={AGE_COLORS[0]} hide={hiddenAgeBars['5-15']} />
                    <Bar dataKey="16-25" stackId="a" fill={AGE_COLORS[1]} hide={hiddenAgeBars['16-25']} />
                    <Bar dataKey="26-35" stackId="a" fill={AGE_COLORS[2]} hide={hiddenAgeBars['26-35']} />
                    <Bar dataKey="36-50" stackId="a" fill={AGE_COLORS[3]} hide={hiddenAgeBars['36-50']} />
                    <Bar dataKey="51-60" stackId="a" fill={AGE_COLORS[4]} hide={hiddenAgeBars['51-60']} />
                    <Bar dataKey="61-70" stackId="a" fill={AGE_COLORS[5]} hide={hiddenAgeBars['61-70']} />
                    <Bar dataKey="71-80" stackId="a" fill={AGE_COLORS[6]} hide={hiddenAgeBars['71-80']} />
                    <Bar dataKey="81+" stackId="a" fill={AGE_COLORS[7]} hide={hiddenAgeBars['81+']} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartSection>

              <TableWrapper>
                <StatsTable>
                  <thead>
                    <tr>
                      <SortableHeader onClick={() => handleSort('year')}>
                        År {sortColumn === 'year' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('totalActiveMembers')}>
                        Aktive medlemmer {sortColumn === 'totalActiveMembers' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('newMembers')}>
                        Nye medlemmer {sortColumn === 'newMembers' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('firstTimeMembers')}>
                        Førstegang {sortColumn === 'firstTimeMembers' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('activeClubs')}>
                        Klubber {sortColumn === 'activeClubs' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('maleCount')}>
                        Menn {sortColumn === 'maleCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('femaleCount')}>
                        Kvinner {sortColumn === 'femaleCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('5-15')}>
                        5-15 {sortColumn === '5-15' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('16-25')}>
                        16-25 {sortColumn === '16-25' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('26-35')}>
                        26-35 {sortColumn === '26-35' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('36-50')}>
                        36-50 {sortColumn === '36-50' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('51-60')}>
                        51-60 {sortColumn === '51-60' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('61-70')}>
                        61-70 {sortColumn === '61-70' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('71-80')}>
                        71-80 {sortColumn === '71-80' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('81+')}>
                        81+ {sortColumn === '81+' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTableData.map((stat) => (
                      <tr key={stat.year}>
                        <td>{stat.year}</td>
                        <td>{stat.totalActiveMembers}</td>
                        <td>{stat.newMembers}</td>
                        <td>{stat.firstTimeMembers}</td>
                        <td>{stat.activeClubs}</td>
                        <td>{stat.maleCount}</td>
                        <td>{stat.femaleCount}</td>
                        <td>{stat.ageGroups['5-15']}</td>
                        <td>{stat.ageGroups['16-25']}</td>
                        <td>{stat.ageGroups['26-35']}</td>
                        <td>{stat.ageGroups['36-50']}</td>
                        <td>{stat.ageGroups['51-60']}</td>
                        <td>{stat.ageGroups['61-70']}</td>
                        <td>{stat.ageGroups['71-80']}</td>
                        <td>{stat.ageGroups['81+']}</td>
                      </tr>
                    ))}
                  </tbody>
                </StatsTable>
              </TableWrapper>
            </>
          )}

          {nsffData.length === 0 && (
            <NoData>Ingen NSFF statistikk tilgjengelig</NoData>
          )}
        </ContentWrapper>
      </Container>
    </>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    > a, > h1 {
      color: ${(p) => p.theme.color.primary.base};
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;

    h1 {
      margin: 0;
      font-size: 1.4rem;
      font-weight: normal;
    }
  }

  > div:nth-child(3) {
    margin-right: 20px;
    > a {
      color: ${(p) => p.theme.color.primary.base};
    }
  }
`;

const Title = styled.h1`
  color: #36501c;
  font-family: 'Fibon Neue';
  margin-bottom: 30px;
  text-align: center;
`;

const NavLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const NavLink = styled(RouterNavLink)`
  padding: 10px 20px;
  border: 2px solid #90b361;
  border-radius: 4px;
  text-decoration: none;
  color: #36501c;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: #90b361;
    color: white;
  }

  &.active {
    background-color: #36501c;
    border-color: #36501c;
    color: white;
  }
`;

const ChartSection = styled.div`
  margin-bottom: 50px;
`;

const ChartTitle = styled.h2`
  color: #36501c;
  font-family: 'Fibon Neue';
  margin-bottom: 20px;
  font-size: 1.3rem;
`;

const TwoColumnSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 50px;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const StatsCard = styled.div`
  background-color: #f9f9f9;
  border: 2px solid #90b361;
  border-radius: 8px;
  padding: 25px;

  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #36501c;
    text-align: center;
  }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
`;

const StatLabel = styled.span`
  color: #666;
  font-weight: 500;
`;

const StatValue = styled.span`
  color: #36501c;
  font-weight: bold;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ddd;
  margin: 15px 0;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 30px;
  -webkit-overflow-scrolling: touch;

  @media ${device.mobile} {
    margin-left: -30px;
    margin-right: -30px;
    padding: 0 15px;
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    white-space: nowrap;
  }

  th {
    background-color: #90b361;
    color: #36501c;
    font-weight: bold;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  td:nth-child(n+2), th:nth-child(n+2) {
    text-align: center;
  }

  @media ${device.mobile} {
    font-size: 11px;

    th, td {
      padding: 8px 4px;
    }
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #7a9851;
  }
`;

const NoData = styled.div`
  text-align: center;
  padding: 50px;
  color: #666;
  font-size: 1.2rem;
`;
